import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { eclipseCopy } from "@/data/eclipseCopy";
import { resourceGuides } from "@/data/resourceGuides";

export const BOOK = "https://calendar.app.google/rYw7YTPaeNN3yf2u5";
export const SQ = "M50 0C90 0 100 10 100 50C100 90 90 100 50 100C10 100 0 90 0 50C0 10 10 0 50 0Z";
export const SLASH =
  "M18.89 80.29Q17.4 84 21.4 84L35.4 84Q39.4 84 40.89 80.29L51.14 54.64Q53 50 58 50L64 50Q69 50 70.86 45.36L81.11 19.71Q82.6 16 78.6 16L64.6 16Q60.6 16 59.11 19.71L48.86 45.36Q47 50 42 50L36 50Q31 50 29.14 54.64Z";
export const ext = { target: "_blank", rel: "noopener noreferrer" } as const;
const mono = "font-ec-mono text-[12px]";
const wrap = "mx-auto max-w-[1200px] px-6";

export const Mark = ({ size, fill = "var(--ec-black)", className, label }: { size: number; fill?: string; className?: string; label?: string }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" className={className} role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
    <path d={SQ} fill={fill} />
    <path d={SLASH} fill="var(--ec-lime)" />
  </svg>
);

export const useEclipseCopy = () => {
  const { i18n } = useTranslation();
  const isDe = !!i18n.language?.startsWith("de");
  return { c: eclipseCopy[isDe ? "de" : "en"], isDe, i18n };
};

/** Section anchors live on the homepage; on subpages they point back to it. */
const useNavLinks = (onHome: boolean) => {
  const { c } = useEclipseCopy();
  const p = onHome ? "" : "/";
  return [
    [`${p}#produkte`, c.nav.products],
    [`${p}#zusammenarbeit`, c.nav.work],
    [`${p}#loop`, c.nav.loop],
    [`${p}#operator`, c.nav.operator],
  ] as const;
};

export const EclipseNav = ({ fixed = false }: { fixed?: boolean }) => {
  const { c, isDe, i18n } = useEclipseCopy();
  const [open, setOpen] = useState(false);
  const links = useNavLinks(!fixed);
  const lang = (
    <button
      type="button"
      onClick={() => i18n.changeLanguage(isDe ? "en" : "de")}
      className={`${mono} text-ec-soft hover:text-ec-ink`}
      aria-label={isDe ? "Switch to English" : "Auf Deutsch wechseln"}
    >
      {isDe ? "EN" : "DE"}
    </button>
  );
  const inner = (
    <>
      <nav className="flex items-center justify-between gap-4">
        <a href="/" aria-label="rawResponse Labs" className="flex items-center gap-2.5 text-ec-ink no-underline">
          <Mark size={32} />
          <span className="whitespace-nowrap text-[15px] font-medium tracking-[-0.02em] sm:text-[17px]">rawResponse Labs</span>
        </a>
        <div className="flex items-center gap-6 text-[14px] font-medium">
          <div className="hidden items-center gap-6 md:flex">
            {links.map(([href, label]) => (
              <a key={href} href={href} className="text-ec-soft no-underline hover:text-ec-ink">{label}</a>
            ))}
            {lang}
          </div>
          <a href={BOOK} {...ext} className="inline-flex rounded-full bg-ec-highlight px-[18px] py-2.5 text-ec-lime-ink no-underline">
            {c.nav.cta}
          </a>
          <button type="button" className="text-ec-soft md:hidden" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="mt-4 flex flex-col gap-4 rounded-[20px] bg-ec-shell-card p-5 text-[15px] font-medium md:hidden">
          {links.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="text-ec-soft no-underline">{label}</a>
          ))}
          <div>{lang}</div>
        </div>
      )}
    </>
  );
  if (!fixed) return inner;
  return (
    <header className="eclipse fixed inset-x-0 top-0 z-50 ec-nav-glass">
      <div className={`${wrap} py-4`}>{inner}</div>
    </header>
  );
};

export const EclipseFooter = ({ onHome = false }: { onHome?: boolean }) => {
  const { c, isDe } = useEclipseCopy();
  const links = useNavLinks(onHome);
  const link = "text-ec-soft no-underline hover:text-ec-ink";
  return (
    <footer className="eclipse relative overflow-hidden border-t border-ec-line-footer bg-ec-footer">
      <div className={`${wrap} pt-24`}>
        <div className="flex flex-wrap justify-between gap-14">
          <div className="flex max-w-[520px] flex-col gap-6" style={{ flex: "1 1 380px" }}>
            <Mark size={44} fill="var(--ec-mark)" label="rawResponse Labs" className="-rotate-[14deg]" />
            <p className="m-0 text-[20px] font-medium leading-[1.4] tracking-[-0.015em] text-ec-soft">
              “Every individual should have the right, choice, and ability to grow a meaningful business and life.{" "}
              <span className="text-ec-muted-2">That's why I build fully autonomous companies, and help striving entrepreneurs do the same.”</span>
            </p>
            <span className={`${mono} text-ec-muted-2`}>Levent Valente, Berlin</span>
          </div>
          <div className="grid gap-8" style={{ flex: "1 1 480px", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
            <div className="flex flex-col gap-3 text-[14px]">
              <span className={`${mono} text-ec-dim`}>{c.footer.page}</span>
              {links.map(([href, label]) => <a key={href} href={href} className={link}>{label}</a>)}
            </div>
            <div className="flex flex-col gap-3 text-[14px]">
              <span className={`${mono} text-ec-dim`}>{c.nav.products}</span>
              <a href="https://webblitz.ai" {...ext} className={link}>webblitz.ai ↗</a>
              <a href="https://chatkmu.de" {...ext} className={link}>chatkmu.de ↗</a>
              <span className="text-ec-muted-2">{c.footer.projectL}</span>
            </div>
            <div className="flex flex-col gap-3 text-[14px]">
              <span className={`${mono} text-ec-dim`}>{c.footer.contact}</span>
              <a href={BOOK} {...ext} className={link}>{c.nav.cta} ↗</a>
              <a href="https://linkedin.com/in/levo" {...ext} className={link}>LinkedIn ↗</a>
              <span className="text-ec-muted-2">Berlin · Frankfurt</span>
            </div>
            {!onHome && (
              <div className="flex flex-col gap-3 text-[14px]">
                <span className={`${mono} text-ec-dim`}>{c.footer.solutions}</span>
                <a href="/solutions/agencies" className={link}>{c.footer.agencies}</a>
                <a href="/solutions/enterprises" className={link}>{c.footer.enterprises}</a>
                <a href="/maschinenbau" className={link}>{c.footer.manufacturing}</a>
                <a href="/resources" className={link}>{c.footer.resources}</a>
                <a href="/about" className={link}>{c.footer.about}</a>
              </div>
            )}
            <div className="flex flex-col gap-3 text-[14px]">
              <span className={`${mono} text-ec-dim`}>{isDe ? "Wissen" : "Guides"}</span>
              <a href="/resources" className={link}>{isDe ? "Alle Guides & FAQ" : "All guides & FAQ"}</a>
              {resourceGuides[isDe ? "de" : "en"].map((guide) => (
                <a key={guide.slug} href={`/resources/${guide.slug}`} className={`${link} max-w-[240px] leading-[1.5]`}>
                  {guide.slug === "agency-blueprint" ? (isDe ? "Agentic Delivery für Agenturen" : "Agentic delivery for agencies") : (isDe ? "KI-Sichtbarkeit messen" : "Measuring AI visibility")}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-[72px] flex flex-wrap justify-between gap-3 border-t border-ec-line-footer pt-5 text-[12px] text-ec-dim">
          <span>rawResponse Labs UG (haftungsbeschränkt) · Urbanstraße 71, 10967 Berlin · HRB 284973 B, Amtsgericht Charlottenburg</span>
          <span className="flex gap-5">
            <a href="/imprint" className="text-ec-muted-2 hover:text-ec-ink">{c.footer.imprint}</a>
            <a href="/privacy" className="text-ec-muted-2 hover:text-ec-ink">{c.footer.privacy}</a>
          </span>
        </div>
      </div>
      <div aria-hidden="true" className="mt-10 flex h-[clamp(62px,8.6vw,130px)] justify-center overflow-hidden">
        <span className="ec-wordmark block whitespace-nowrap text-[clamp(74px,10.6vw,166px)] font-semibold leading-[0.82] tracking-[-0.06em]">
          rawResponse <span className="font-normal">Labs</span>
        </span>
      </div>
    </footer>
  );
};
